import { Button, Form, Input } from 'antd';
import type { TExportPayoutInvoicesBody } from 'api/invoices/payout/types';
import { useTranslation } from 'react-i18next';
import { usePayoutInvoicesTableStore } from 'store/payoutInvoicesTable';
import { usePayoutInvoicesTableFiltersStore } from 'store/payoutInvoicesTableFilters';
import { errorHelper } from 'utils/helpers/errorHelper';
import type { TComponent } from 'utils/types/component';

import { TimePickerWithHint } from 'components/ui/TimePickerWithHint';

import { PAYOUT_INVOICE_SCHEDULE_TYPE } from 'models/Invoice/Payout';

import { MonthlyList } from './components/MonthlyList';
import { PeriodicitySelect } from './components/PeriodicitySelect';
import { WeekList } from './components/WeekList';
import styles from './CreateSettingsForm.module.scss';
import { createSettingsFormHelpers } from './helpers';
import type { IForm } from './types';

interface ICreateSettingsFormProps {
    onClose: () => void;
}

export const CreateSettingsForm: TComponent<ICreateSettingsFormProps> = (
    props,
) => {
    const { onClose } = props;

    const exportPayoutInvoices = usePayoutInvoicesTableStore(
        'exportPayoutInvoices',
    );
    const filters = usePayoutInvoicesTableFiltersStore('body');

    const { t } = useTranslation();

    const [form] = Form.useForm();

    const scheduleTypeValue = Form.useWatch<PAYOUT_INVOICE_SCHEDULE_TYPE>(
        'scheduleType',
        form,
    );

    const isDailyScheduleType =
        scheduleTypeValue === PAYOUT_INVOICE_SCHEDULE_TYPE.DAILY;
    const isWeekScheduleType =
        scheduleTypeValue === PAYOUT_INVOICE_SCHEDULE_TYPE.WEEKLY ||
        scheduleTypeValue === PAYOUT_INVOICE_SCHEDULE_TYPE.EVEN_WEEKS ||
        scheduleTypeValue === PAYOUT_INVOICE_SCHEDULE_TYPE.ODD_WEEKS;
    const isMonthlyScheduleType =
        scheduleTypeValue === PAYOUT_INVOICE_SCHEDULE_TYPE.MONTHLY;

    const handleFinish = async (values: IForm) => {
        const bodyForBackend: TExportPayoutInvoicesBody =
            createSettingsFormHelpers.createSettingsPayload(
                values,
                scheduleTypeValue,
                filters,
            );

        try {
            await exportPayoutInvoices(bodyForBackend);

            onClose();
        } catch (error) {
            errorHelper.showApiErrorNotification(error);
        }
    };

    return (
        <Form
            form={form}
            layout='vertical'
            onFinish={handleFinish}
            className={styles.form}
            initialValues={{
                dateFrom: null,
                dateTo: null,
            }}
        >
            <Form.Item
                name='email'
                label={t('common.form.enterEmail')}
                rules={[
                    {
                        required: true,
                        message: t('common.form_error.email_required'),
                    },
                    {
                        type: 'email',
                        message: t('common.form_error.email_wrong'),
                    },
                ]}
            >
                <Input placeholder='example@test.com' />
            </Form.Item>

            <PeriodicitySelect />

            {isDailyScheduleType && (
                <Form.Item
                    label={t('common.form.timeToSend')}
                    name='dailyTime'
                    rules={[
                        {
                            required: true,
                            message: t('common.form.chooseTimeToSend'),
                        },
                    ]}
                >
                    <TimePickerWithHint />
                </Form.Item>
            )}

            {isWeekScheduleType && <WeekList />}

            {isMonthlyScheduleType && <MonthlyList />}

            <Form.Item className={styles.sendBtnContainer}>
                <Button
                    className={styles.sendBtn}
                    htmlType='submit'
                    type='primary'
                >
                    {t('common.buttons.send')}
                </Button>
            </Form.Item>
        </Form>
    );
};
